import { z } from 'zod';
import { displayNameSchema, emailSchema, isoDateTimeSchema, localeSchema, timeZoneSchema, uuidSchema, } from '../common/primitives.js';
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 256;
/** Length-based policy (NIST SP 800-63B): no composition rules, long passphrases welcome. */
export const newPasswordSchema = z
    .string()
    .min(PASSWORD_MIN_LENGTH, `Use at least ${String(PASSWORD_MIN_LENGTH)} characters`)
    .max(PASSWORD_MAX_LENGTH)
    .refine((value) => value.trim().length >= PASSWORD_MIN_LENGTH, {
    message: `Use at least ${String(PASSWORD_MIN_LENGTH)} non-space characters`,
});
/** Existing passwords are only length-capped; policy applies when a password is set. */
export const currentPasswordSchema = z.string().min(1).max(PASSWORD_MAX_LENGTH);
/** 6-digit TOTP code or a recovery code (`xxxxx-xxxxx`). */
export const mfaCodeSchema = z
    .string()
    .trim()
    .min(6)
    .max(32)
    .regex(/^[0-9]{6}$|^[a-z0-9]{5}-?[a-z0-9]{5}$/i, 'Enter the 6-digit code or a recovery code');
export const singleUseTokenSchema = z
    .string()
    .min(32)
    .max(128)
    .regex(/^[A-Za-z0-9_-]+$/);
export const AUTH_METHODS = ['PASSWORD', 'MAGIC_LINK', 'SSO'];
export const authMethodSchema = z.enum(AUTH_METHODS);
export const loginRequestSchema = z.object({
    email: emailSchema,
    password: currentPasswordSchema,
});
export const mfaLoginRequestSchema = z.object({
    challengeToken: z.string().min(16).max(1024),
    code: mfaCodeSchema,
});
export const emailRequestSchema = z.object({ email: emailSchema });
export const tokenRequestSchema = z.object({ token: singleUseTokenSchema });
export const passwordResetRequestSchema = z.object({
    token: singleUseTokenSchema,
    newPassword: newPasswordSchema,
});
export const passwordChangeRequestSchema = z.object({
    currentPassword: currentPasswordSchema,
    newPassword: newPasswordSchema,
});
/** Step-up: prove possession of the password or a second factor again. */
export const reauthenticateRequestSchema = z.union([
    z.object({ password: currentPasswordSchema }).strict(),
    z.object({ code: mfaCodeSchema }).strict(),
]);
export const sessionInfoSchema = z.object({
    id: uuidSchema,
    authMethod: authMethodSchema,
    lastAuthenticatedAt: isoDateTimeSchema,
    mfaVerifiedAt: isoDateTimeSchema.nullable(),
    /** Sensitive actions are allowed until this instant without re-authentication. */
    recentAuthUntil: isoDateTimeSchema,
});
export const currentUserSchema = z.object({
    id: uuidSchema,
    email: z.string(),
    displayName: z.string(),
    locale: localeSchema,
    timezone: z.string(),
    mfaEnabled: z.boolean(),
    hasPassword: z.boolean(),
    lastLoginAt: isoDateTimeSchema.nullable(),
    session: sessionInfoSchema,
});
export const loginResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('AUTHENTICATED'), user: currentUserSchema }),
    z.object({
        status: z.literal('MFA_REQUIRED'),
        challengeToken: z.string(),
        expiresAt: isoDateTimeSchema,
    }),
]);
/** Returned for requests that must not reveal whether an account exists. */
export const acceptedResponseSchema = z.object({ accepted: z.literal(true) });
export const updateMyProfileRequestSchema = z
    .object({
    displayName: displayNameSchema.optional(),
    locale: localeSchema.optional(),
    timezone: timeZoneSchema.optional(),
})
    .refine((value) => Object.keys(value).length > 0, { message: 'Nothing to update' });
export const deviceSessionSchema = z.object({
    id: uuidSchema,
    current: z.boolean(),
    authMethod: authMethodSchema,
    userAgent: z.string().nullable(),
    ipAddress: z.string().nullable(),
    createdAt: isoDateTimeSchema,
    lastUsedAt: isoDateTimeSchema.nullable(),
    expiresAt: isoDateTimeSchema,
    mfaVerified: z.boolean(),
});
export const deviceSessionListSchema = z.object({ items: z.array(deviceSessionSchema) });
export const revokedCountSchema = z.object({ revoked: z.number().int().nonnegative() });
export const totpSetupResponseSchema = z.object({
    /** Base32 secret for manual entry; shown once and never logged. */
    secret: z.string(),
    otpauthUri: z.string(),
});
export const mfaCodeRequestSchema = z.object({ code: mfaCodeSchema });
export const recoveryCodesResponseSchema = z.object({ recoveryCodes: z.array(z.string()) });
export const mfaStatusSchema = z.object({
    totpEnabled: z.boolean(),
    recoveryCodesRemaining: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map